import React, { useMemo } from 'react';

import { Grid, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import type { ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import type { VersionCardVersion } from './VersionCard';
import { VersionCard } from './VersionCard';

const useStyles = makeStyles()(theme => ({
  month: {
    margin: theme.spacing(1, 0),
  },
}));

interface VersionCardListProps {
  // Versions ordered by versionNum
  orderedVersions: VersionCardVersion[];
  setMonthDayTuple: ReactSetStateTuple<MonthDay | null>;
  latestVersionId?: UUID;
}

/**
 * Nested object in the shape of: { 'December': { 'December 1': [version1, version2] } }
 */
type GroupedVersions = Record<string, Record<string, VersionCardVersion[]>>;
export interface MonthDay {
  day: string;
  month: string;
}

export function VersionCardList({
  latestVersionId,
  orderedVersions,
  setMonthDayTuple,
}: VersionCardListProps): JSX.Element {
  const { classes } = useStyles();
  const [monthDay, setMonthDay] = setMonthDayTuple;
  const groupedVersions = useMemo(() => {
    return orderedVersions.reduce<GroupedVersions>((groupedVersions, version) => {
      const monthString = format(parseISO(version.createdAt), 'MMMM');
      const monthDayString = format(parseISO(version.createdAt), 'MMMM d');
      if (!groupedVersions[monthString]) {
        groupedVersions[monthString] = {};
      }
      const monthGroup = groupedVersions[monthString];
      if (!monthGroup[monthDayString]) {
        monthGroup[monthDayString] = [];
      }
      monthGroup[monthDayString].push(version);
      return groupedVersions;
    }, {});
  }, [orderedVersions]);

  if (monthDay) {
    const { day, month } = monthDay;
    const dayVersions = groupedVersions[month][day];

    return (
      <Grid container sx={{ flexDirection: 'column' }}>
        {dayVersions.map(version => {
          return (
            <VersionCard
              key={version.id}
              day={day}
              isLatestVersion={latestVersionId === version.id}
              month={month}
              setMonthDay={setMonthDay}
              totalNumVersions={orderedVersions.length}
              versionsGroupedByDay={[version]}
            />
          );
        })}
      </Grid>
    );
  }

  return (
    <>
      {Object.entries(groupedVersions).map(([month, monthVersions], monthIndex) => {
        return (
          <Grid key={monthIndex} container sx={{ flexDirection: 'column' }}>
            <Typography className={classes.month} variant="caption">
              {month}
            </Typography>
            {Object.entries(monthVersions).map(([day, versionsGroupedByDay]) => {
              const [firstVersion] = versionsGroupedByDay;

              return (
                <VersionCard
                  key={firstVersion.id}
                  day={day}
                  isLatestVersion={versionsGroupedByDay.some(
                    version => latestVersionId === version.id
                  )}
                  month={month}
                  setMonthDay={setMonthDay}
                  totalNumVersions={orderedVersions.length}
                  versionsGroupedByDay={versionsGroupedByDay}
                />
              );
            })}
          </Grid>
        );
      })}
    </>
  );
}
